import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { formatPrice, productImage, shopGet } from "../../api/shopApi";

export default function ShopProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState("");

  useSeo(
    item
      ? {
          title: item.seo_title || `${item.name} - 生活好物商城`,
          metaDescription: item.seo_description || item.subtitle || item.name,
          path: `/shop/product/${slug}`,
          ogImage: item.main_image || "",
        }
      : { title: "商品详情", path: `/shop/product/${slug}`, noindex: true }
  );

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    shopGet(`/products/${encodeURIComponent(slug)}`)
      .then((data) => {
        setItem(data);
        setActiveImg(data.main_image || data.images?.[0]?.image_url || "");
      })
      .catch((e) => setError(e.message || "商品不存在"))
      .finally(() => setLoading(false));
  }, [slug]);

  const goCheckout = () => {
    if (!item) return;
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent(`/shop/checkout?product=${item.id}&slug=${item.slug}&qty=${qty}`)}`
      );
      return;
    }
    navigate(`/shop/checkout?product=${item.id}&slug=${item.slug}&qty=${qty}`);
  };

  const gallery = item
    ? [
        ...(item.main_image ? [item.main_image] : []),
        ...((item.images || []).map((i) => i.image_url) || []),
      ].filter((v, i, arr) => v && arr.indexOf(v) === i)
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <Link to="/shop" className="text-sm text-pink-600 hover:underline">
            ← 返回商城
          </Link>
          {loading ? (
            <p className="text-center text-gray-400 py-16">加载中…</p>
          ) : error || !item ? (
            <p className="text-center text-gray-500 py-16">{error || "商品不存在"}</p>
          ) : (
            <div className="mt-4 grid md:grid-cols-2 gap-8 bg-white rounded-3xl shadow-lg p-6 md:p-8">
              <div className="space-y-3">
                <div className="aspect-square rounded-2xl overflow-hidden bg-rose-50">
                  <img
                    src={productImage(activeImg || item.main_image)}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {gallery.length > 1 ? (
                  <div className="flex gap-2 overflow-x-auto">
                    {gallery.map((url) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setActiveImg(url)}
                        className={`w-16 h-16 rounded-lg overflow-hidden border shrink-0 ${
                          activeImg === url ? "border-pink-500" : "border-transparent"
                        }`}
                      >
                        <img src={productImage(url)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="space-y-4">
                {item.category_name ? (
                  <Link
                    to={`/shop/category/${item.category_slug}`}
                    className="text-sm text-pink-500 hover:underline"
                  >
                    {item.category_name}
                  </Link>
                ) : null}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{item.name}</h1>
                {item.subtitle ? <p className="text-gray-500">{item.subtitle}</p> : null}
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-bold text-rose-600">{formatPrice(item.price)}</span>
                  {item.compare_at_price ? (
                    <span className="text-gray-400 line-through">{formatPrice(item.compare_at_price)}</span>
                  ) : null}
                </div>
                {!item.in_stock ? (
                  <p className="text-amber-700 text-sm">暂时缺货</p>
                ) : (
                  <p className="text-emerald-700 text-sm">可提交购买意向</p>
                )}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">数量</span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                    className="w-20 border rounded-lg px-2 py-1.5 text-sm"
                  />
                </div>
                <button
                  type="button"
                  disabled={!item.in_stock}
                  onClick={goCheckout}
                  className="w-full py-3.5 rounded-full bg-pink-500 text-white font-semibold disabled:opacity-50"
                >
                  提交购买意向
                </button>
                <p className="text-xs text-gray-400 leading-relaxed">
                  当前为人工确认订单，暂不在线支付。提交后平台将核实商品供应、价格和发货信息。
                </p>
                <dl className="text-sm text-gray-600 space-y-2 border-t pt-4">
                  {item.brand ? (
                    <div>
                      <dt className="inline text-gray-400">品牌：</dt>
                      <dd className="inline">{item.brand}</dd>
                    </div>
                  ) : null}
                  {item.specifications ? (
                    <div>
                      <dt className="text-gray-400 mb-1">规格</dt>
                      <dd className="whitespace-pre-wrap">
                        {typeof item.specifications === "string"
                          ? item.specifications
                          : JSON.stringify(item.specifications, null, 2)}
                      </dd>
                    </div>
                  ) : null}
                  {item.manufacturer ? <p>生产企业：{item.manufacturer}</p> : null}
                  {item.origin ? <p>产地：{item.origin}</p> : null}
                  {item.product_license ? <p>资质信息：{item.product_license}</p> : null}
                </dl>
              </div>
              <section className="md:col-span-2 border-t pt-6 space-y-4">
                <h2 className="text-lg font-bold text-gray-800">商品详情</h2>
                <div className="text-gray-700 text-[15px] leading-8 whitespace-pre-wrap">
                  {item.description || "暂无详细介绍"}
                </div>
                <h2 className="text-lg font-bold text-gray-800">发货说明</h2>
                <p className="text-sm text-gray-600 leading-7 whitespace-pre-wrap">
                  {item.shipping_note || "运费与发货安排以平台确认信息为准。"}
                </p>
                {item.after_sale_note ? (
                  <>
                    <h2 className="text-lg font-bold text-gray-800">售后说明</h2>
                    <p className="text-sm text-gray-600 leading-7 whitespace-pre-wrap">{item.after_sale_note}</p>
                  </>
                ) : null}
              </section>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
